import type {
  AdminDebugCredentials,
  AuthenticationCredentials,
  RegistrationCredentials
} from "../../../commons/types/credentials";

/**
 * Keys of cookies used for storing credentials, excluding "serviceTag".
 * We exclude "serviceTag" because it is not stored as a cookie.
 */
type CookieKey = Exclude<
  keyof AuthenticationCredentials | keyof RegistrationCredentials | keyof AdminDebugCredentials,
  "serviceTag"
>;

const isLocationHttps = (): boolean => {
  return window.location.protocol === "https:";
};

const setCookie = (key: CookieKey, value: string, expires?: string) => {
  const secureFlag = isLocationHttps() ? "Secure;" : "";
  const expiresFlag = expires ? `expires=${expires};` : "";
  document.cookie = `${key}=${value};SameSite=Strict;${secureFlag}${expiresFlag}`;
};

const deleteCookie = (key: CookieKey) => {
  // Cookie deletion by setting expiration date in the past
  setCookie(key, "", "Thu, 01 Jan 1970 00:00:00 GMT");
};

export const setLicenseCookies = ({ licenseId, licenseToken }: AuthenticationCredentials) => {
  setCookie("licenseId", licenseId);
  setCookie("licenseToken", licenseToken);
};

export const setRegistrationCookie = (registrationToken: string) => {
  setCookie("registrationToken", registrationToken);
};

export const deleteLicenseCookies = () => {
  deleteCookie("licenseId");
  deleteCookie("licenseToken");
};

export const setAdminDebugCookie = (timestamp: number, signature: string) => {
  // Session cookie: no explicit expires. Server enforces TTL.
  setCookie("adminDebug", `${timestamp}.${signature}`);
};

export const deleteAdminDebugCookie = () => {
  deleteCookie("adminDebug");
};

export const hasAdminDebugCookie = (): boolean => {
  return document.cookie.split(";").some(cookie => cookie.trim().startsWith("adminDebug="));
};
